import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api, formatMoney } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Minus, Trash2, ShoppingBag, Coffee, CreditCard, Banknote, Check, Printer, X, Smartphone, Wallet } from "lucide-react";
import { printThermal, esc, rs } from "@/lib/thermal";
import AddExpenseModal from "@/components/AddExpenseModal";

const lineKey = (productId, variantName) => `${productId}::${variantName || ""}`;

export default function POS() {
  const { outlet } = useOutletContext();
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]); // { productId, productName, variantName, unitPrice, qty }
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [flashKey, setFlashKey] = useState(null);
  const [lastOrder, setLastOrder] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [expenseOpen, setExpenseOpen] = useState(false);

  useEffect(() => {
    if (!outlet) return;
    api.get(`/categories?outlet_id=${outlet.id}`).then(({ data }) => {
      setCategories(data);
      if (data.length && !activeCat) setActiveCat(data[0].id);
    });
    api.get(`/products?outlet_id=${outlet.id}`).then(({ data }) => setProducts(data));
    // eslint-disable-next-line
  }, [outlet]);

  const filteredProducts = useMemo(
    () => products.filter((p) => (activeCat ? p.category_id === activeCat : true) && p.active),
    [products, activeCat]
  );

  const addLine = (product, variant) => {
    const variantName = variant?.name || null;
    const unitPrice = variant ? variant.price : product.price;
    const key = lineKey(product.id, variantName);
    setCart((c) => {
      const idx = c.findIndex((it) => lineKey(it.productId, it.variantName) === key);
      if (idx >= 0) {
        const next = [...c];
        next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
        return next;
      }
      return [...c, {
        productId: product.id,
        productName: product.name,
        variantName,
        unitPrice,
        qty: 1,
      }];
    });
    setFlashKey(key);
    setTimeout(() => setFlashKey(null), 500);
  };

  const changeQty = (key, delta) => {
    setCart((c) =>
      c
        .map((it) => (lineKey(it.productId, it.variantName) === key ? { ...it, qty: it.qty + delta } : it))
        .filter((it) => it.qty > 0)
    );
  };

  const removeLine = (key) =>
    setCart((c) => c.filter((it) => lineKey(it.productId, it.variantName) !== key));

  const subtotal = cart.reduce((s, it) => s + it.unitPrice * it.qty, 0);

  const finalize = async (payment) => {
    try {
      const items = cart.map((it) => ({
        product_id: it.productId,
        variant_name: it.variantName || undefined,
        qty: it.qty,
      }));
      const { data: order } = await api.post("/orders", { outlet_id: outlet.id, items, payment_method: payment });
      order.outlet_name = outlet.name;
      setLastOrder(order);
      toast.success(`Order paid (${payment.toUpperCase()}) · ₹${formatMoney(subtotal)}`);
      setCart([]);
      setCheckoutOpen(false);
      setShowReceipt(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create order");
    }
  };

  if (!outlet) return <div className="p-8 text-muted-foreground">Loading outlet…</div>;

  return (
    <div className="h-full flex overflow-hidden">
      {/* Categories */}
      <aside className="w-24 md:w-40 lg:w-52 border-r border-border overflow-y-auto thin-scroll bg-background p-3 flex flex-col gap-1" data-testid="categories-sidebar">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground px-3 py-2">
          Categories
        </div>
        {categories.length === 0 && (
          <div className="text-xs text-muted-foreground px-3 py-2">No categories yet.</div>
        )}
        {categories.map((c) => {
          const active = c.id === activeCat;
          return (
            <button
              key={c.id}
              data-testid={`category-btn-${c.id}`}
              onClick={() => setActiveCat(c.id)}
              className={`w-full text-left px-4 py-4 border transition-colors font-heading ${
                active
                  ? "bg-card border-border border-l-4 border-l-primary font-semibold text-foreground"
                  : "border-transparent text-muted-foreground hover:bg-muted"
              }`}
            >
              {c.name}
            </button>
          );
        })}
        <button
          data-testid="pos-add-expense-btn"
          onClick={() => setExpenseOpen(true)}
          className="mt-auto w-full flex items-center justify-center gap-2 px-3 py-3 border border-dashed border-border text-xs font-heading uppercase tracking-widest text-muted-foreground hover:text-foreground hover:border-primary hover:bg-card transition-colors"
        >
          <Wallet strokeWidth={1.5} className="w-4 h-4" /> <span className="hidden md:inline">Expense</span>
        </button>
      </aside>

      {/* Products */}
      <section className="flex-1 overflow-y-auto thin-scroll p-4 md:p-6" data-testid="products-area">
        {categories.length === 0 ? (
          <EmptyMenu />
        ) : filteredProducts.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center gap-4 max-w-md mx-auto">
            <Coffee strokeWidth={1.25} className="w-16 h-16 text-muted-foreground" />
            <h3 className="font-heading text-2xl">No items in this category</h3>
            <p className="text-sm text-muted-foreground">Add items from the Admin panel to start selling.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-testid="products-grid">
            {filteredProducts.map((p) => (
              <ProductTile
                key={p.id}
                product={p}
                onAdd={addLine}
                flashKey={flashKey}
              />
            ))}
          </div>
        )}
      </section>

      {/* Cart */}
      <aside className="w-80 lg:w-96 border-l border-border bg-card flex flex-col shrink-0" data-testid="cart-sidebar">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingBag strokeWidth={1.5} className="w-4 h-4" />
            <span className="text-xs font-mono uppercase tracking-widest">Current Order</span>
          </div>
          <span className="text-xs text-muted-foreground font-mono" data-testid="cart-count">
            {cart.reduce((s, it) => s + it.qty, 0)} items
          </span>
        </div>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center gap-3 text-muted-foreground">
            <ShoppingBag strokeWidth={1.25} className="w-10 h-10" />
            <p className="text-sm">Tap items to build an order</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto thin-scroll" data-testid="cart-items">
            {cart.map((it) => {
              const key = lineKey(it.productId, it.variantName);
              return (
                <div
                  key={key}
                  className={`px-5 py-4 border-b border-border ${flashKey === key ? "animate-flash-tint" : ""}`}
                  data-testid={`cart-item-${key}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-heading font-semibold text-sm">
                        {it.productName}
                        {it.variantName && (
                          <span className="ml-1.5 text-[10px] font-mono uppercase tracking-widest text-primary">
                            · {it.variantName}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono">
                        ₹{formatMoney(it.unitPrice)} each
                      </div>
                    </div>
                    <div className="font-mono text-sm font-semibold">
                      ₹{formatMoney(it.unitPrice * it.qty)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      data-testid={`cart-dec-${key}`}
                      onClick={() => changeQty(key, -1)}
                      className="w-8 h-8 flex items-center justify-center border border-border bg-background hover:bg-muted active:scale-95 transition-transform"
                    >
                      <Minus strokeWidth={1.5} className="w-3.5 h-3.5" />
                    </button>
                    <span className="w-8 text-center font-mono text-sm" data-testid={`cart-qty-${key}`}>
                      {it.qty}
                    </span>
                    <button
                      data-testid={`cart-inc-${key}`}
                      onClick={() => changeQty(key, 1)}
                      className="w-8 h-8 flex items-center justify-center border border-border bg-background hover:bg-muted active:scale-95 transition-transform"
                    >
                      <Plus strokeWidth={1.5} className="w-3.5 h-3.5" />
                    </button>
                    <button
                      data-testid={`cart-remove-${key}`}
                      onClick={() => removeLine(key)}
                      className="ml-auto text-destructive hover:opacity-80 text-xs flex items-center gap-1"
                    >
                      <Trash2 strokeWidth={1.5} className="w-3.5 h-3.5" /> Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="border-t border-border p-5 pb-16 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="font-mono" data-testid="cart-subtotal">₹{formatMoney(subtotal)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-heading text-lg font-semibold">Total</span>
            <span className="font-mono text-2xl font-bold" data-testid="cart-total">₹{formatMoney(subtotal)}</span>
          </div>
          <button
            data-testid="cart-checkout-btn"
            onClick={() => setCheckoutOpen(true)}
            disabled={cart.length === 0}
            className="w-full bg-primary text-primary-foreground py-4 font-heading font-semibold uppercase tracking-[0.15em] text-sm hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-40 flex items-center justify-center gap-2"
          >
            <Check strokeWidth={1.75} className="w-4 h-4" /> Checkout
          </button>
        </div>
      </aside>

      {checkoutOpen && (
        <CheckoutModal
          total={subtotal}
          onCancel={() => setCheckoutOpen(false)}
          onPay={finalize}
        />
      )}

      {expenseOpen && <AddExpenseModal outlet={outlet} onClose={() => setExpenseOpen(false)} />}

      {showReceipt && lastOrder && (
        <ReceiptModal order={lastOrder} onClose={() => setShowReceipt(false)} />
      )}
    </div>
  );
}

function ProductTile({ product, onAdd, flashKey }) {
  const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;
  const isFlashing = flashKey && flashKey.startsWith(`${product.id}::`);
  const priceText = hasVariants
    ? `₹${formatMoney(Math.min(...product.variants.map((v) => v.price)))}+`
    : `₹${formatMoney(product.price)}`;

  const tileClasses = `relative flex flex-col h-44 bg-card border border-border overflow-hidden select-none text-left transition-transform duration-100 ${
    isFlashing ? "ring-2 ring-primary" : ""
  }`;

  const imageBlock = (
    <div className="h-20 w-full bg-muted overflow-hidden shrink-0">
      {product.image_url ? (
        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
          <Coffee strokeWidth={1.25} className="w-8 h-8" />
        </div>
      )}
    </div>
  );

  if (!hasVariants) {
    return (
      <button
        data-testid={`product-tile-${product.id}`}
        onClick={() => onAdd(product, null)}
        className={`${tileClasses} cursor-pointer active:scale-[0.97]`}
      >
        {imageBlock}
        <div className="flex-1 px-3 py-2 flex flex-col justify-between">
          <div className="font-heading font-semibold text-sm leading-tight line-clamp-2">
            {product.name}
          </div>
          <div className="text-xs font-mono text-muted-foreground">{priceText}</div>
        </div>
      </button>
    );
  }

  // Variant tile: not a single button — chips are individual buttons
  return (
    <div data-testid={`product-tile-${product.id}`} className={tileClasses}>
      {imageBlock}
      <div className="flex-1 px-3 py-2 flex flex-col justify-between gap-1">
        <div className="font-heading font-semibold text-sm leading-tight line-clamp-1">
          {product.name}
        </div>
        <div className="flex gap-1 flex-wrap">
          {product.variants.map((v) => (
            <button
              key={v.name}
              data-testid={`variant-chip-${product.id}-${v.name}`}
              onClick={() => onAdd(product, v)}
              className="flex-1 min-w-0 border border-border bg-background hover:bg-primary hover:text-primary-foreground hover:border-primary active:scale-95 transition-all px-1.5 py-1 text-[11px] font-heading uppercase tracking-wider flex flex-col items-center justify-center"
            >
              <span className="leading-none">{v.name.charAt(0)}</span>
              <span className="font-mono normal-case tracking-normal text-[10px] mt-0.5 opacity-80">
                ₹{formatMoney(v.price)}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmptyMenu() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center max-w-md mx-auto gap-6" data-testid="empty-menu">
      <img
        src="https://images.unsplash.com/photo-1498604636225-6b87a314baa0?w=600&auto=format"
        alt="Cup of tea"
        className="w-56 h-56 object-cover border border-border grayscale opacity-70"
      />
      <div>
        <h2 className="font-heading text-3xl font-bold mb-2">Fresh terminal, empty menu.</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Head to <span className="font-semibold text-foreground">Admin</span> to add your first category and product.
          Tea, coffee, snacks — whatever you brew.
        </p>
      </div>
    </div>
  );
}

function CheckoutModal({ total, onCancel, onPay }) {
  const [busy, setBusy] = useState(false);
  const pay = async (method) => {
    setBusy(true);
    await onPay(method);
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 flex items-center justify-center p-4" onClick={onCancel} data-testid="checkout-modal">
      <div className="bg-card border border-border w-full max-w-sm p-6 animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Amount due</div>
        <div className="font-mono text-4xl font-bold mb-6" data-testid="checkout-total">₹{formatMoney(total)}</div>
        <div className="grid grid-cols-3 gap-3">
          <button
            data-testid="pay-cash-btn"
            disabled={busy}
            onClick={() => pay("cash")}
            className="flex flex-col items-center gap-2 border border-border p-5 hover:bg-muted active:scale-[0.98] transition-all disabled:opacity-40"
          >
            <Banknote strokeWidth={1.5} className="w-6 h-6" />
            <span className="font-heading font-semibold text-sm uppercase tracking-wider">Cash</span>
          </button>
          <button
            data-testid="pay-card-btn"
            disabled={busy}
            onClick={() => pay("card")}
            className="flex flex-col items-center gap-2 border border-border p-5 hover:bg-muted active:scale-[0.98] transition-all disabled:opacity-40"
          >
            <CreditCard strokeWidth={1.5} className="w-6 h-6" />
            <span className="font-heading font-semibold text-sm uppercase tracking-wider">Card</span>
          </button>
          <button
            data-testid="pay-upi-btn"
            disabled={busy}
            onClick={() => pay("upi")}
            className="flex flex-col items-center gap-2 border border-border p-5 hover:bg-muted active:scale-[0.98] transition-all disabled:opacity-40"
          >
            <Smartphone strokeWidth={1.5} className="w-6 h-6" />
            <span className="font-heading font-semibold text-sm uppercase tracking-wider">UPI</span>
          </button>
        </div>
        <button
          data-testid="checkout-cancel-btn"
          onClick={onCancel}
          className="w-full mt-4 py-2 text-sm text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function ReceiptModal({ order, onClose }) {
  const print = () => printThermal(`Receipt ${order.id.slice(0, 8)}`, buildReceiptHTML(order));
  const dt = new Date(order.created_at);
  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 flex items-center justify-center p-4" onClick={onClose} data-testid="receipt-modal">
      <div className="bg-card border border-border w-full max-w-sm p-0 animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="font-heading text-lg font-semibold">Order paid</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              #{order.id.slice(0, 8)} · {order.outlet_name}
            </div>
          </div>
          <button data-testid="receipt-close" onClick={onClose} className="p-1 hover:bg-muted">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 text-sm space-y-3" style={{ fontFamily: "Arial, Helvetica, sans-serif", fontVariantNumeric: "tabular-nums" }}>
          <div className="flex justify-between text-muted-foreground text-xs">
            <span>{dt.toLocaleString()}</span>
            <span className="uppercase font-semibold">{order.payment_method}</span>
          </div>
          <div className="border-t border-dashed border-border pt-3 space-y-1.5">
            {order.items.map((it, i) => (
              <div key={i} className="flex justify-between gap-2">
                <span className="flex-1"><span className="font-semibold">{it.qty}×</span> {it.name}</span>
                <span className="font-semibold">₹{formatMoney(it.line_total)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-dashed border-border pt-3 flex justify-between text-lg font-bold">
            <span>TOTAL</span>
            <span>₹{formatMoney(order.total)}</span>
          </div>
          {order.cashier_name && (
            <div className="text-muted-foreground text-xs pt-2 text-center">
              Served by <span className="font-semibold">{order.cashier_name}</span>
            </div>
          )}
          <div className="text-center text-[9px] italic text-muted-foreground tracking-widest pt-1">
            chai for everyone
          </div>
        </div>
        <div className="border-t border-border p-4 grid grid-cols-2 gap-2">
          <button
            data-testid="receipt-print-btn"
            onClick={print}
            className="flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 text-xs font-heading uppercase tracking-widest hover:opacity-90 active:scale-[0.98]"
          >
            <Printer strokeWidth={1.75} className="w-4 h-4" /> Print
          </button>
          <button
            data-testid="receipt-done-btn"
            onClick={onClose}
            className="flex items-center justify-center gap-2 border border-border py-3 text-xs font-heading uppercase tracking-widest hover:bg-muted active:scale-[0.98]"
          >
            <Check strokeWidth={1.75} className="w-4 h-4" /> Done
          </button>
        </div>
      </div>
    </div>
  );
}

function buildReceiptHTML(order) {
  const dt = new Date(order.created_at);
  const rows = order.items.map((it) =>
    `<tr><td class="qty">${it.qty}×</td><td class="name">${esc(it.name)}</td><td class="amt">${rs(it.line_total)}</td></tr>`
  ).join("");
  return `<h1>TEABOY</h1>
<div class="tag">${esc(order.outlet_name || "")}</div>
<div class="meta">Bill #${order.id.slice(0,8).toUpperCase()}<br/><b>${dt.toLocaleString()}</b><br/>Payment: <b>${String(order.payment_method).toUpperCase()}</b></div>
<hr/>
<table>${rows}</table>
<hr/>
<table><tr class="total"><td>TOTAL</td><td></td><td class="amt">${rs(order.total)}</td></tr></table>
<hr/>
<div class="foot">${order.cashier_name ? "Served by " + esc(order.cashier_name) : ""}</div>
<div class="thanks">Thank you — visit again!</div>
<div class="tagline">chai for everyone</div>`;
}
